/**
 * What the login page sends to Supabase Auth. Mirrors normalize_login_username() in the database
 * (migration 20261105000001): keep both in sync.
 *
 * Accounts without a real mailbox (ministers, ushers) log in with a username; Auth still needs an
 * email, so the account has an internal address <username>@login.invalid that users never see.
 */

export const INTERNAL_LOGIN_DOMAIN = 'login.invalid';

/**
 * The number of a CI: the first run of 5+ digits, ignoring dots. Department ("SC") and complement
 * ("-1A") are not part of it. "1234567 SC" -> 1234567, "E-12345678" -> 12345678.
 */
export function ciNumber(text: string | null | undefined): string | null {
  const match = (text ?? '').replace(/\./g, '').match(/[0-9]{5,}/);
  return match ? match[0] : null;
}

/** Normalized login identifier: an email as typed, an ADB code, a CI number or a plain username */
export function normalizeLoginIdentifier(input: string): string {
  const v = input.trim().toLowerCase();
  if (!v) return '';
  if (v.includes('@')) return v;
  if (/^[0-9]{2}-[0-9]+$/.test(v)) return v;
  const n = ciNumber(v);
  if (n && !/^[a-z]{3,}/.test(v)) return n;
  return v.replace(/\s+/g, '');
}

/** The email to send to Auth: real emails stay as typed, usernames get the internal domain */
export function toAuthEmail(input: string): string {
  const v = normalizeLoginIdentifier(input);
  if (!v) return '';
  return v.includes('@') ? v : `${v}@${INTERNAL_LOGIN_DOMAIN}`;
}

export const isInternalEmail = (email: string | null | undefined) =>
  (email ?? '').toLowerCase().endsWith(`@${INTERNAL_LOGIN_DOMAIN}`);
